import React, { useState, useEffect } from 'react';
import { database, auth } from '../firebase';
import { ref, onValue, off } from 'firebase/database';
import { onAuthStateChanged } from 'firebase/auth';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

// Helper: Extract city from address
const getCityFromAddress = (address) => {
  if (!address) return 'Unknown City';
  const addressParts = address.trim().split(/\s+/);
  return addressParts.length > 0 ? addressParts[addressParts.length - 1] : 'Unknown City';
};

// Filter Section Component
const FilterSection = ({ title, options, selected, onChange }) => {
  const handleCheckboxChange = (option) => {
    if (option === options[0]) { // "All" option
      if (selected.includes(option)) {
        onChange([]);
      } else {
        onChange([...options]);
      }
    } else {
      const newSelected = selected.includes(option)
        ? selected.filter(item => item !== option && item !== options[0])
        : [...selected.filter(item => item !== options[0]), option];
      
      if (newSelected.length === options.length - 1) {
        onChange([options[0], ...newSelected]);
      } else {
        onChange(newSelected);
      }
    }
  };

  return (
    <div style={{
      backgroundColor: '#f8fafc',
      padding: '16px',
      borderRadius: '8px',
      border: '1px solid #e2e8f0',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.3s ease',
      maxWidth: '220px',
      marginBottom: '16px',
    }}>
      <h2 style={{
        fontSize: '16px',
        fontWeight: '600',
        color: '#1e293b',
        marginBottom: '8px',
        borderBottom: '2px solid #3b82f6',
        paddingBottom: '6px',
      }}>{title}</h2>
      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {options.map((option) => (
          <label key={option} style={{ 
            display: 'flex', 
            alignItems: 'center', 
            gap: '8px',
            cursor: 'pointer',
            padding: '6px',
            borderRadius: '6px',
            transition: 'background-color 0.2s',
            backgroundColor: selected.includes(option) ? '#eff6ff' : 'transparent',
          }}
          onMouseEnter={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
            }
          }}
          onMouseLeave={(e) => {
            if (!selected.includes(option)) {
              e.currentTarget.style.backgroundColor = 'transparent';
            }
          }}
          >
            <input
              type="checkbox"
              checked={selected.includes(option)}
              onChange={() => handleCheckboxChange(option)}
              style={{
                height: '14px',
                width: '14px',
                accentColor: '#3b82f6',
                cursor: 'pointer',
              }}
            />
            <span style={{ 
              fontSize: '14px',
              color: '#475569',
              userSelect: 'none',
              fontWeight: selected.includes(option) ? '500' : '400',
            }}>{option}</span>
          </label>
        ))}
      </div>
    </div>
  );
};

// Bottom Selling Products Display Component
const BottomSellingProductsDisplay = ({ filters, branchProfile, salesData, productsData }) => {
  const filteredData = salesData.filter(sale => {
    let matchesFilters = true;
    if (filters.customer.length > 0 && !filters.customer.includes("All Customers")) {
      matchesFilters = matchesFilters && filters.customer.includes(sale.customer || "Unknown Customer");
    }
    if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
      matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
    }
    if (filters.fromDate && filters.toDate) {
      const saleDate = new Date(sale.date.split('-').reverse().join('-'));
      const fromDate = new Date(filters.fromDate);
      const toDate = new Date(filters.toDate);
      matchesFilters = matchesFilters && saleDate >= fromDate && saleDate <= toDate;
    }
    return matchesFilters;
  });

  // Calculate product sales with product codes
  const productSales = {};
  
  // Only add products that have actual sales
  filteredData.forEach(sale => {
    sale.items.forEach(item => {
      const productName = item.itemDescription || 'Unknown Product';
      const qty = parseFloat(item.qty) || 0;
      
      // Find the product in productsData to get the correct P.ID
      const product = productsData.find(p => 
        p.productName && p.productName.toLowerCase() === productName.toLowerCase()
      );
      
      // Use the pCode from product table as the product code
      const productCode = product ? product.pCode : (item.productCode || item.barCode || item.barcode || item.itemCode || 'Unknown Code');
      
      // Use product name as key to group by product, then store the first product code found
      if (!productSales[productName]) {
        productSales[productName] = { 
          productCode: productCode, 
          productName: productName, 
          totalSold: 0 
        };
      }
      productSales[productName].totalSold += qty;
    });
  });

  // Get products that have sold less than 10 units
  const bottomProducts = Object.values(productSales)
    .filter(product => product.totalSold > 0 && product.totalSold < 10)
    .sort((a, b) => a.totalSold - b.totalSold);

  return (
    <div style={{
      backgroundColor: 'white',
      padding: '24px',
      marginTop: '24px',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      fontSize: '14px',
      pageBreakInside: 'avoid',
      border: '1px solid #e2e8f0',
      borderRadius: '8px',
      boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
    }} className="print-only">
      <table style={{
        width: '100%',
        borderCollapse: 'collapse',
        border: '1px solid #d1d5db',
      }}>
        <thead>
          <tr style={{ backgroundColor: '#f0f9ff', borderBottom: '2px solid #3b82f6' }}>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>PRODUCT CODE</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>PRODUCT NAME</th>
            <th style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', fontWeight: '600', color: '#1e293b' }}>TOTAL SOLD</th>
          </tr>
        </thead>
        <tbody>
          {bottomProducts.length === 0 ? (
            <tr>
              <td colSpan={3} style={{
                border: '1px solid #d1d5db',
                padding: '24px',
                textAlign: 'center',
                color: '#6b7280',
                fontStyle: 'italic',
                fontSize: '14px',
              }}>
                No products found with sales less than 10 units
              </td>
            </tr>
          ) : (
            bottomProducts.map((product, index) => (
              <tr key={index}>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{product.productCode}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{product.productName}</td>
                <td style={{ border: '1px solid #d1d5db', padding: '12px', textAlign: 'center', color: '#374151' }}>{product.totalSold.toFixed(2)}</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
};

// Main Bottom Selling Products Generator Component
const BottomSellingProductsGenerator = () => {
  const [filters, setFilters] = useState({
    fromDate: '',
    toDate: '',
    branch: ["All Branches"],
    customer: ["All Customers"]
  });
  const [branches, setBranches] = useState(["All Branches"]);
  const [customers, setCustomers] = useState(["All Customers"]);
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showReport, setShowReport] = useState(false);
  const [branchProfile, setBranchProfile] = useState({
    branchName: "SUPREME TRADERS",
    address: "",
    phone: "",
    city: ""
  });
  const [salesData, setSalesData] = useState([]);
  const [productsData, setProductsData] = useState([]);

  const encodeEmailForPath = (email) => {
    return email.replace(/[.#$[\]]/g, '_');
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      if (user) {
        loadFirebaseData(user);
      }
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const loadFirebaseData = (user) => {
    const encodedEmail = encodeEmailForPath(user.email);

    const branchProfileRef = ref(database, `users/${encodedEmail}/branchProfiles`);
    onValue(branchProfileRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const profiles = Object.values(data);
        const latestProfile = profiles[profiles.length - 1];
        setBranchProfile({
          branchName: latestProfile.branchName || "SUPREME TRADERS",
          address: `${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`.trim(),
          phone: latestProfile.mobileNo || '',
          city: latestProfile.city || getCityFromAddress(`${latestProfile.address1 || ''} ${latestProfile.address2 || ''}`)
        });
        setBranches(["All Branches", latestProfile.branchName || "SUPREME TRADERS"]);
      }
    });

    const customersRef = ref(database, `users/${encodedEmail}/customers`);
    onValue(customersRef, (snapshot) => {
      const customersData = ["All Customers"];
      if (snapshot.exists()) {
        snapshot.forEach((childSnapshot) => {
          const customer = childSnapshot.val();
          if (customer.customerName && !customersData.includes(customer.customerName)) {
            customersData.push(customer.customerName);
          }
        });
      }
      setCustomers(customersData);
      if (!filters.customer.length) {
        setFilters(prev => ({ ...prev, customer: [customersData[0]] }));
      }
    });

    const productsRef = ref(database, `users/${encodedEmail}/products`);
    onValue(productsRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const productList = Object.entries(data).map(([id, product]) => ({
          id,
          productName: product.productName || 'Unknown Product',
          productCode: product.productCode || product.pCode || product.productId || id,
          barcode: product.barcode || '',
          hsnCode: product.hsnSacCode || product.hsnCode || '',
          pCode: product.pCode || product.productCode || product.productId || id
        }));
        setProductsData(productList);
      } else {
        setProductsData([]);
      }
    });

    const salesRef = ref(database, `users/${encodedEmail}/sales`);
    onValue(salesRef, (snapshot) => {
      const data = snapshot.val();
      if (data) {
        const salesRecords = Object.entries(data).map(([id, sale]) => ({
          id,
          billNo: sale.billNo || 'N/A',
          date: sale.date || 'N/A',
          customer: sale.customer || 'Unknown Customer',
          totals: sale.totals || { netAmount: 0 },
          items: sale.items || []
        }));
        setSalesData(salesRecords);
      } else {
        setSalesData([]);
      }
    });

    return () => {
      off(branchProfileRef);
      off(customersRef);
      off(productsRef);
      off(salesRef);
    };
  };

  const handleFilterChange = (key, value) => {
    setFilters({ ...filters, [key]: value });
  };

  const handleGenerateReport = () => {
    setShowReport(true);
  };

  const getBottomSellingProductsData = () => {
    const filteredData = salesData.filter(sale => {
      let matchesFilters = true;
      if (filters.customer.length > 0 && !filters.customer.includes("All Customers")) {
        matchesFilters = matchesFilters && filters.customer.includes(sale.customer || "Unknown Customer");
      }
      if (filters.branch.length > 0 && !filters.branch.includes("All Branches")) {
        matchesFilters = matchesFilters && filters.branch.includes(branchProfile.branchName || "Unknown Branch");
      }
      if (filters.fromDate && filters.toDate) {
        const saleDate = new Date(sale.date.split('-').reverse().join('-'));
        const fromDate = new Date(filters.fromDate);
        const toDate = new Date(filters.toDate);
        matchesFilters = matchesFilters && saleDate >= fromDate && saleDate <= toDate;
      }
      return matchesFilters;
    });

    const productSalesForExport = {};
    
    filteredData.forEach(sale => {
      sale.items.forEach(item => {
        const productName = item.itemDescription || 'Unknown Product';
        const qty = parseFloat(item.qty) || 0;
        
        const product = productsData.find(p => 
          p.productName && p.productName.toLowerCase() === productName.toLowerCase()
        );
        
        const productCode = product ? product.pCode : (item.productCode || item.barCode || item.barcode || item.itemCode || 'Unknown Code');
        
        if (!productSalesForExport[productName]) {
          productSalesForExport[productName] = {
            productCode: productCode,
            productName: productName,
            totalSold: 0
          };
        }
        productSalesForExport[productName].totalSold += qty;
      });
    });

    return Object.values(productSalesForExport)
      .filter(product => product.totalSold > 0 && product.totalSold < 10)
      .sort((a, b) => a.totalSold - b.totalSold);
  };

  const handleDownloadExcel = () => {
    const bottomProducts = getBottomSellingProductsData();
    
    const productsSheetData = bottomProducts.map(product => ({
      'Product Code': product.productCode,
      'Product Name': product.productName,
      'Total Sold': product.totalSold
    }));

    const workbook = XLSX.utils.book_new();
    const productsSheet = XLSX.utils.json_to_sheet(productsSheetData);
    XLSX.utils.book_append_sheet(workbook, productsSheet, 'Bottom Selling Products');
    XLSX.writeFile(workbook, 'Bottom Selling Products.xlsx');
  };

  const handleDownloadPDF = () => {
    const doc = new jsPDF();
    doc.setFont('helvetica');
    doc.setFontSize(16);
    doc.text('Bottom Selling Products', 14, 15);
    
    doc.setFontSize(12);
    doc.text(`From Date: ${filters.fromDate || 'N/A'}`, 14, 25);
    doc.text(`To Date: ${filters.toDate || 'N/A'}`, 14, 32);
    doc.text(`Branch: ${branchProfile.branchName || 'N/A'}`, 14, 39);

    const bottomProducts = getBottomSellingProductsData();
    
    const productsDataForPDF = bottomProducts.map(product => [
      product.productCode,
      product.productName,
      product.totalSold.toFixed(2)
    ]);

    autoTable(doc, {
      startY: 45,
      head: [['PRODUCT CODE', 'PRODUCT NAME', 'TOTAL SOLD']],
      body: productsDataForPDF,
      theme: 'grid',
      headStyles: { 
        fillColor: [59, 130, 246],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        fontSize: 10
      },
      bodyStyles: {
        fontSize: 9
      },
      columnStyles: {
        0: { cellWidth: 40, halign: 'center' },
        1: { cellWidth: 80, halign: 'left' },
        2: { cellWidth: 30, halign: 'center' }
      },
      alternateRowStyles: {
        fillColor: [245, 245, 245]
      },
      margin: { top: 45, left: 14, right: 14 }
    });

    doc.save('Bottom_Selling_Products_Less_Than_10.pdf');
  };

  if (loading) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '16px' }}>Loading...</div>;
  }

  if (!currentUser) {
    return <div style={{ textAlign: 'center', padding: '2rem', color: '#6b7280', fontFamily: 'system-ui, -apple-system, sans-serif', fontSize: '16px' }}>Please login to access the Bottom Selling Products</div>;
  }

  return (
    <div style={{
      width: '100%',
      maxWidth: '90rem',
      backgroundColor: 'white',
      borderRadius: '16px',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      padding: '32px',
      margin: '0 auto',
      fontFamily: 'system-ui, -apple-system, sans-serif',
      border: '1px solid #e2e8f0'
    }}>
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @media print {
            body * {
              visibility: hidden;
            }
            .print-only, .print-only * {
              visibility: visible;
            }
            .print-only {
              position: absolute;
              left: 0;
              top: 0;
              width: 100%;
              margin: 0;
              padding: 20px;
              box-shadow: none;
              border: none;
            }
          }
        `}
      </style>
      
      <div style={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: '12px',
        marginBottom: '24px',
        paddingBottom: '16px',
        borderBottom: '2px solid #e2e8f0'
      }}>
        <div style={{
          width: '6px',
          height: '32px',
          backgroundColor: '#3b82f6',
          borderRadius: '3px'
        }}></div>
        <h1 style={{
          fontSize: '28px',
          fontWeight: '700',
          color: '#1e293b',
          margin: '0'
        }}>Bottom Selling Products</h1>
      </div>
      
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
        gap: '20px',
        marginBottom: '32px',
        padding: '24px',
        backgroundColor: '#f8fafc',
        borderRadius: '12px',
        border: '1px solid #e2e8f0'
      }}>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>From Date</label>
          <input
            type="date"
            value={filters.fromDate}
            onChange={(e) => handleFilterChange('fromDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
        <div>
          <label style={{
            display: 'block',
            fontSize: '14px',
            fontWeight: '600',
            color: '#374151',
            marginBottom: '8px'
          }}>To Date</label>
          <input
            type="date"
            value={filters.toDate}
            onChange={(e) => handleFilterChange('toDate', e.target.value)}
            style={{
              width: '100%',
              padding: '12px 16px',
              border: '1px solid #d1d5db',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              transition: 'all 0.2s',
              backgroundColor: 'white',
              cursor: 'pointer',
              boxSizing: 'border-box'
            }}
            onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
            onBlur={(e) => e.target.style.borderColor = '#d1d5db'}
          />
        </div>
      </div>
    <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(2, minmax(240px, 1fr))',
        gap: '16px',
        marginBottom: '24px',
        whiteSpace: 'nowrap'
      }}>
        <FilterSection title="Branches" options={branches} selected={filters.branch} onChange={(value) => handleFilterChange("branch", value)} />
        <FilterSection title="Customers" options={customers} selected={filters.customer} onChange={(value) => handleFilterChange("customer", value)} />
      </div>
      <div style={{ 
        display: 'flex', 
        gap: '16px', 
        marginBottom: '32px',
        justifyContent: 'center'
      }}>
        <button
          onClick={handleGenerateReport}
          style={{
            background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Generate Report
        </button>
        <button
          onClick={handleDownloadExcel}
          style={{
            background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download Excel
        </button>
        <button
          onClick={handleDownloadPDF}
          style={{
            background: 'linear-gradient(135deg, #6b7280 0%, #4b5563 100%)',
            color: 'white',
            padding: '12px 24px',
            borderRadius: '8px',
            border: 'none',
            cursor: 'pointer',
            transition: 'all 0.3s',
            fontSize: '16px',
            fontWeight: '600',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            minWidth: '120px',
            boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = 'translateY(-2px)';
            e.currentTarget.style.boxShadow = '0 6px 12px rgba(0, 0, 0, 0.15)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = 'translateY(0)';
            e.currentTarget.style.boxShadow = '0 4px 6px rgba(0, 0, 0, 0.1)';
          }}
        >
          Download PDF
        </button>
      </div>
      
      {showReport && (
        <BottomSellingProductsDisplay
          filters={filters}
          branchProfile={branchProfile}
          salesData={salesData}
          productsData={productsData}
        />
      )}
    </div>
  );
};

export default BottomSellingProductsGenerator;